import { createStore } from 'react-sweet-state';
import { cloneDeep, orderBy } from 'lodash';

import {
  GENERATION_TOOL,
  getMultiPromptSubtools,
  getToolClassifiedMixtureSubGenerationTools,
  getToolDefaultAssetType,
  getToolMixtureSubGenerationTools,
  isMixtureTool,
  isRewriteGenerationTool,
} from 'src/data/generationTool';
import {
  addLanguageToGenerationResults,
  VARIATION_RATING,
  VARIATION_STATUS,
  variationRatingTextToValue,
} from 'src/data/variation';
import { saveImageVariation } from 'src/graphql/generation';
import { queryGenerationResults } from 'src/graphql/project';
import {
  archiveVariations,
  deleteVariationRatings,
  publishVariations,
  rateVariation,
  updateAllProjectVariations,
  updateVariationImageMeta,
  variationFeedbackReasons,
} from 'src/graphql/variation';
import { parseJson } from 'src/lib/common';
import { getParsedGraphQLFirstError, unwrapEdges } from 'src/lib/graphql';
import { captureMessage } from 'src/lib/sentry';
import { pluralize } from 'src/utils/format';

const INITIAL_STATE = {
  lastGenerationTimestamp: undefined,

  generationResults: [],
  landingPageAssetGenerationResults: [],
  loadingGenerationResults: false,
  generationResultsLoaded: false,

  editedVariationIds: [],
  detailsVisibleForVariationId: null,

  hiddenGenerationWarnings: [],

  ratingsBannerShown: false,
  didClearRatings: false,
  cursor: null,
  hasNextPage: null,
  isFetching: false,
  variationWarning: null,
};

const listAdjust = (list, id, isAdding) =>
  isAdding ? [...list, id] : list.filter((n) => n !== id);

const mixtureResultsWithOrderIdx = (result) => {
  const sorted = result.variations.sort((a, b) => {
    // Sorts by order of assetTypes in tool props and then by score
    const genTool = a.generationTool;
    const mixtureTools = [
      ...getToolMixtureSubGenerationTools(genTool),
      ...getToolClassifiedMixtureSubGenerationTools(genTool),
    ];
    const assetTypes = mixtureTools.map((tool) => getToolDefaultAssetType(tool));
    return assetTypes.indexOf(a.assetType) - assetTypes.indexOf(b.assetType) || b.score - a.score;
  });

  // add groupOrderIdx to variations
  const groupIdxToOrderIdx = {};
  let setGroupIdx;

  const sortedWithGroupOrderIdx = sorted.map((variation, idx) => {
    if (!Object.keys(groupIdxToOrderIdx).includes(variation.groupIdx[0].toString())) {
      setGroupIdx = true;
    } else {
      setGroupIdx = false;
    }

    if (setGroupIdx) {
      groupIdxToOrderIdx[variation.groupIdx[0]] = idx;
      variation.groupOrderIdx = [idx];
    } else {
      variation.groupOrderIdx = [groupIdxToOrderIdx[variation.groupIdx[0]]];
    }
    return variation;
  });

  return { ...result, variations: sortedWithGroupOrderIdx };
};

const addVariationAfterId = (obj, targetId, newVariations) => {
  const stack = [];
  for (const result of obj) {
    stack.push(...result.variations);
    while (stack.length > 0) {
      const variation = stack.pop();

      if (variation.id === targetId) {
        // Add the new variations after the target id

        if (!variation?.variations) {
          variation.variations = [];
        }
        variation.variations = [...newVariations, ...variation.variations];
        return obj;
      }

      if (variation.variations && variation.variations.length > 0) {
        // Add nested variations to the stack
        stack.push(...variation.variations);
      }
    }
  }
};

const prepareVariation = (variation) => {
  const imageMeta = variation.imageMeta ? parseJson(variation.imageMeta) : {};
  return {
    ...variation,
    imageMeta,
  };
};

const prepareResults = (results) => {
  return results.map((result) => {
    return {
      ...result,
      variations: result.variations.map((variation) => prepareVariation(variation)),
    };
  });
};

const sortGenerationResults = (results, filterEmpty) => {
  const filteredResults = filterEmpty
    ? results.filter((result) => {
        return result.variations.length;
      })
    : results;

  const markedErrorResults = markErrorResults(filteredResults);

  // some results have internal originalVariationId (only at the variation level) for some of their variations,
  // these will be added as nested variations after removing the variations without originalVariationId
  const resultsWithInternalOriginalVariationId = cloneDeep(markedErrorResults)
    .filter((result) => {
      return !result.originalVariationId && result?.variations?.some((v) => v.originalVariationId);
    })
    .map((item) => {
      item.variations = item.variations.filter((v) => v.originalVariationId);
      item.originalVariationId = item.variations[0].originalVariationId;
      return item;
    });

  // These will be added as nested variations to the original id variation
  const resultsWithOriginalVariationId = markedErrorResults
    .filter((result) => {
      return result.originalVariationId;
    })
    .concat(resultsWithInternalOriginalVariationId);

  const sortedWithOriginalVariationId = orderBy(
    resultsWithOriginalVariationId,
    ['createdAt', 'id'],
    ['asc', 'asc']
  );

  // These are original variations only
  const resultsWithoutOriginalVariationId = markedErrorResults
    .filter((result) => {
      return !result.originalVariationId;
    })
    .map((result) => {
      // remove mixed variations from original variations
      const variations = result.variations.filter((variation) => !variation.originalVariationId);
      return { ...result, variations };
    });

  const resultsWithoutChainId = resultsWithoutOriginalVariationId.filter((result) => {
    return !result.chainId;
  });
  let sortedWithoutChainId = orderBy(resultsWithoutChainId, ['createdAt', 'id'], ['desc', 'desc']);

  const resultsWithChainId = resultsWithoutOriginalVariationId.filter((result) => {
    return result.chainId;
  });

  const sortedWithChainId = orderBy(resultsWithChainId, ['chainId', 'createdAt'], ['desc', 'desc']);

  const finalSorted = [];

  if (resultsWithChainId.length) {
    // first, need to group results by chain id, while also keeping track of created at edges. Each chainId should have a single createdAt edge.

    const createdAtEdges = {};
    const chainIdSubToolResults = {};
    const chainIdGenerationTool = {};
    sortedWithChainId.forEach((res) => {
      if (!chainIdSubToolResults[res.chainId]) {
        chainIdSubToolResults[res.chainId] = [];
        chainIdGenerationTool[res.chainId] = res.chainGenerationTool;
      }
      chainIdSubToolResults[res.chainId].push(res);

      if (!Object.keys(createdAtEdges).includes(res.chainId)) {
        createdAtEdges[res.chainId] = res.createdAt;
      }
    });

    // sort the subTool results by blockIds or fall back the order of the subTools in the getMultiPromptSubtools
    Object.keys(chainIdSubToolResults).forEach((res) => {
      chainIdSubToolResults[res] = chainIdSubToolResults[res].sort((a, b) => {
        if (a.blockId && b.blockId) {
          return a.blockId - b.blockId;
        }
        const promptTools = getMultiPromptSubtools(chainIdGenerationTool[res]);
        return (
          Object.values(promptTools).indexOf(a.generationTool) -
          Object.values(promptTools).indexOf(b.generationTool)
        );
      });
    });

    // iterate over createdAtEdges (newest first), and for each createdAtEdge, add all results that are greater than that createdAtEdge, else add all subTool results for that chainId and delete them from the chainIdSubToolResults object

    Object.keys(createdAtEdges)
      .reverse()
      .forEach((chainId) => {
        sortedWithoutChainId.forEach((res, idx) => {
          if (res.createdAt > createdAtEdges[chainId]) {
            finalSorted.push(res);
            sortedWithoutChainId[idx] = null;
          } else {
            if (chainIdSubToolResults[chainId]) {
              chainIdSubToolResults[chainId].forEach((res) => {
                finalSorted.push(res);
              });
              delete chainIdSubToolResults[chainId];
            }
          }
        });
        sortedWithoutChainId = sortedWithoutChainId.filter((res) => res !== null);
      });

    // add any remaining subTool results to the end of the list
    Object.keys(chainIdSubToolResults)
      .reverse()
      .forEach((chainId) => {
        chainIdSubToolResults[chainId].forEach((res) => {
          finalSorted.push(res);
        });
      });
    // add any remaining non subTool results to the end of the list
    finalSorted.push(...sortedWithoutChainId);
  } else {
    finalSorted.push(...sortedWithoutChainId);
  }

  // create a map of variationIds to originalVariationIds
  const variationIdsToOriginalId = sortedWithOriginalVariationId.reduce((acc, result) => {
    result.variations.forEach((v) => {
      acc[v.id] = result.id;
    });
    return acc;
  }, {});

  // iterate over the sortedWithOriginalVariationId and add the variations to the original variation
  for (const resultWithOriginalVariationId of sortedWithOriginalVariationId) {
    const originalResultId =
      variationIdsToOriginalId[resultWithOriginalVariationId.originalVariationId];

    if (originalResultId) {
      const originalResult = sortedWithOriginalVariationId.find((r) => r.id === originalResultId);
      const originalVariation = originalResult.variations.find(
        (v) => v.id === resultWithOriginalVariationId.originalVariationId
      );

      const currentVariations = originalVariation?.variations || [];
      originalVariation.variations = [
        ...currentVariations,
        ...resultWithOriginalVariationId.variations,
      ];
      originalResult.variations = originalResult.variations.map((r) => {
        return { ...r, groupIdx: [originalVariation.id] };
      });
    }
  }

  for (let result of finalSorted) {
    // add order idx to mixture tool variations for grouping
    if (
      isMixtureTool(result.generationTool) &&
      result.generationTool !== GENERATION_TOOL.googleRSA
    ) {
      result = mixtureResultsWithOrderIdx(result);
    }

    // add nested variations to results
    for (const resultWithOriginalVariationId of sortedWithOriginalVariationId) {
      if (
        result.variations
          .map((v) => v.id)
          .includes(resultWithOriginalVariationId.originalVariationId)
      ) {
        const originalVariation = result.variations.find(
          (v) => v.id === resultWithOriginalVariationId.originalVariationId
        );

        const currentVariations = originalVariation?.variations || [];

        originalVariation.variations = [
          ...resultWithOriginalVariationId.variations,
          ...currentVariations,
        ];
      }
    }
  }
  return finalSorted;
};

const hasNonArchivedVariations = (variations) => {
  for (const variation of variations) {
    if (variation.status !== 'archived') {
      return true;
    }

    if (variation?.variations?.length) {
      return hasNonArchivedVariations(variation?.variations);
    }
  }

  return false;
};

const clearRatingForNestedVariationsByGenerationTool = (variations, generationTool) => {
  return variations.map((variation) => {
    if (variation.generationTool === generationTool) {
      variation.rating = VARIATION_RATING.none;
      if (variation?.variations) {
        clearRatingForNestedVariationsByGenerationTool(variation.variations, generationTool);
      }
    }
    return variation;
  });
};

const updatePropertyInNestedVariationById = (variations, variationId, property, value) => {
  for (const variation of variations) {
    if (variation.id === variationId) {
      variation[property] = value;
    }
    if (variation?.variations) {
      updatePropertyInNestedVariationById(variation?.variations, variationId, property, value);
    }
  }

  return variations;
};

const updateNestedVariation = (variations, updatedVariation) => {
  for (const [index, variation] of variations.entries()) {
    if (variation.id === updatedVariation.id) {
      variations[index] = updatedVariation;
    }
    if (variation?.variations) {
      updateNestedVariation(variation?.variations, updatedVariation);
    }
  }

  return variations;
};

const includesNestedVariation = (variations, variationId) => {
  for (const variation of variations) {
    if (variation.id === variationId) {
      return true;
    }
    if (variation?.variations?.length) {
      if (includesNestedVariation(variation?.variations, variationId)) {
        return true;
      }
    }
  }
  return false;
};

const markErrorResults = (results) => {
  return results.map((result) => {
    return { ...result, errored: !result.variations.length };
  });
};

export const PRIVATE_ACTIONS = {
  init:
    () =>
    ({ setState }) => {
      setState(cloneDeep(INITIAL_STATE));
    },

  setVariationEdited:
    (variationId, value) =>
    ({ getState, setState }) => {
      const { editedVariationIds } = getState();
      const newList = listAdjust(editedVariationIds, variationId, value);
      setState({ editedVariationIds: newList });
    },

  updateVariationInGenerationResults:
    (variation) =>
    ({ getState, setState }) => {
      const { generationResults } = getState();

      // Update variation in generationResults list
      const newResults = generationResults.map((generationResult) => {
        const variations = generationResult.variations.map((currentVariation) => {
          if (currentVariation.id === variation.id) {
            return {
              ...currentVariation,
              ...variation,
            };
          }
          return currentVariation;
        });

        return {
          ...generationResult,
          variations,
        };
      });
      setState({ generationResults: newResults });
    },
};

/**
 * Public available actions
 */
const ACTIONS = {
  queryGenerationResults:
    () =>
    ({ setState, getState, dispatch }, { project, notifications }) => {
      setState({ loadingGenerationResults: true, generationResults: [] });

      let isGenerating = false;

      setState({ isFetching: true });

      return queryGenerationResults(project.id)
        .then(({ edges, pageInfo }) => {
          const results = unwrapEdges(edges);
          if (results?.[0]?.isGenerating) {
            isGenerating = true;
            return results;
          }
          const preparedResults = prepareResults(results);
          const sorted = sortGenerationResults(preparedResults, true);
          const withLanguage = addLanguageToGenerationResults(sorted);
          const cursor = pageInfo?.endCursor;
          const hasNextPage = pageInfo?.hasNextPage;
          setState({ generationResults: withLanguage, cursor, hasNextPage });
        })
        .catch((error) => {
          const parsedError = getParsedGraphQLFirstError(error);
          notifications.displayError(parsedError);
          setState({ generationResults: [] });
        })
        .finally(() => {
          if (!isGenerating) {
            setState({
              loadingGenerationResults: false,
              generationResultsLoaded: true,
              isFetching: false,
            });
          }
        });
    },

  queryMoreGenerationResults:
    () =>
    async ({ setState, getState, dispatch }, { project, notifications }) => {
      const cursor = getState().cursor;
      const hasNextPage = getState().hasNextPage;
      const isFetching = getState().isFetching;

      if (isFetching || !hasNextPage) {
        return;
      }

      let isGenerating = false;

      setState({ isFetching: true });
      try {
        const { edges, pageInfo } = await queryGenerationResults(project.id, null, cursor);

        const results = unwrapEdges(edges);
        if (results?.[0]?.isGenerating) {
          isGenerating = true;
          return results;
        }
        const preparedResults = prepareResults(results);
        const sorted = sortGenerationResults(preparedResults, true);
        const withLanguage = addLanguageToGenerationResults(sorted);
        dispatch(ACTIONS.addGenerationResults(withLanguage));
        setState({
          cursor: pageInfo?.endCursor,
          hasNextPage: pageInfo?.hasNextPage,
        });
      } catch (e) {
        notifications.displayError(e);
      } finally {
        if (!isGenerating) {
          setState({
            loadingGenerationResults: false,
            generationResultsLoaded: true,
            isFetching: false,
          });
        }
      }
    },

  hideFlaggedVariation:
    (variationId) =>
    ({ getState, setState }) => {
      const { generationResults } = getState();

      // Search for "result" which contains specified variation
      const result = generationResults.find((r) => r.variations.some((v) => v.id === variationId));
      if (!result) {
        return;
      }

      const newVariations = result.variations
        // Try to remove variation from "parent" ones
        .filter((v) => v.id !== variationId);

      // Replace "result" object in array
      const newResults = generationResults.map((r) =>
        r.id === result.id
          ? {
              ...r,
              variations: newVariations,
            }
          : r
      );
      setState({ generationResults: newResults });
    },

  unHighlightVariations:
    (variationId) =>
    ({ getState, setState }) => {
      const { generationResults } = getState();
      const result = generationResults.find((r) =>
        includesNestedVariation(r.variations, variationId)
      );

      if (!result) {
        return;
      }

      const newVariations = updatePropertyInNestedVariationById(
        result.variations,
        variationId,
        'shouldHighlight',
        false
      );
      // Replace "result" object in array
      const newResults = generationResults.map((r) =>
        r.id === result.id
          ? {
              ...r,
              variations: newVariations,
            }
          : r
      );
      setState({ generationResults: newResults });
    },

  deleteVariations:
    (ids) =>
    async ({ dispatch }, { notifications }) => {
      try {
        await archiveVariations({ ids });

        ids.forEach((id) => {
          dispatch(ACTIONS.archiveVariation(id));
        });
        const postfix = pluralize(ids.length, 'Variation', 'Variations');
        notifications.displaySuccess(`${postfix} deleted`);
      } catch (error) {
        notifications.displayError('Could not delete variations. Please try again.');
        captureMessage("Couldn't delete variation", {
          level: 'warning',
          extra: {
            error: error.toString(),
            source: 'ListViewStore -> deleteVariations',
          },
        });
      }
    },

  archiveVariation:
    (variationId) =>
    ({ getState, setState }) => {
      const { generationResults } = getState();

      // Search for "result" which contains specified variation
      const result = generationResults.find((r) =>
        includesNestedVariation(r.variations, variationId)
      );

      if (!result) {
        return;
      }

      const newVariations = updatePropertyInNestedVariationById(
        result.variations,
        variationId,
        'status',
        VARIATION_STATUS.archived
      );

      const allDeleted = !hasNonArchivedVariations(result.variations);

      // Replace "result" object in array
      const newResults = generationResults.map((r) =>
        r.id === result.id
          ? {
              ...r,
              variations: newVariations,
              allDeleted,
            }
          : r
      );
      setState({ generationResults: newResults });
    },

  markVariationEdited:
    (variationId) =>
    ({ dispatch }) => {
      dispatch(PRIVATE_ACTIONS.setVariationEdited(variationId, true));
    },

  setLastGeneratedTimestamp:
    (timestamp) =>
    ({ setState }) => {
      setState({
        lastGenerationTimestamp: timestamp,
      });
    },

  addGenerationResult:
    (result) =>
    ({ getState, setState, dispatch }) => {
      if (
        result?.variations?.length === 1 &&
        !result?.variations?.[0]?.originalVariationId &&
        !result.variation?.[0]?.variations?.length &&
        (isRewriteGenerationTool(result?.generationTool) ||
          result?.generationTool === GENERATION_TOOL.basicContentImprover)
      ) {
        dispatch(
          ACTIONS.setVariationWarning({
            id: result.variations[0].id,
            reason: result.mainFewerReceivedReason,
            blacklistMatches: result.blacklistMatches,
          })
        );
      } else {
        dispatch(ACTIONS.setVariationWarning(null));
      }

      const { generationResults } = getState();
      const preparedResults = prepareResults([result]);
      const newList = sortGenerationResults([...preparedResults, ...generationResults]);
      setState({
        generationResults: newList,
      });
    },

  addGenerationResults:
    (results) =>
    ({ getState, setState }) => {
      const { generationResults } = getState();
      const preparedResults = prepareResults(results);
      const newList = sortGenerationResults([...preparedResults, ...generationResults]);
      setState({
        generationResults: newList,
      });
    },

  addGenerationSubResult:
    (result) =>
    ({ getState, setState }) => {
      const { generationResults } = getState();
      const resultsClone = [...generationResults];

      result.variations = result.variations.map((variation) => ({
        ...prepareVariation(variation),
        shouldHighlight: true,
      }));

      const newList = addVariationAfterId(
        resultsClone,
        result.originalVariationId,
        result.variations
      );
      setState({
        generationResults: newList,
      });
    },

  clearGenerationResults:
    () =>
    ({ setState }) => {
      setState({
        generationResults: [],
      });
    },

  clearZeroResult:
    (resultId) =>
    ({ getState, setState }) => {
      const { generationResults } = getState();
      const newResults = generationResults.filter((result) => result.id !== resultId);
      setState({ generationResults: newResults });
    },

  updateVariation:
    (variation) =>
    ({ dispatch }) => {
      dispatch(PRIVATE_ACTIONS.updateVariationInGenerationResults(variation));
    },

  openVariationDetailsPanel:
    (variationId) =>
    ({ setState }) => {
      setState({ detailsVisibleForVariationId: variationId });
    },
  closeVariationDetailsPanel:
    () =>
    ({ setState }) => {
      setState({ detailsVisibleForVariationId: null });
    },
  isDetailsOpenedForVariationId:
    (id) =>
    ({ getState }) => {
      const { detailsVisibleForVariationId } = getState();
      return detailsVisibleForVariationId === id;
    },
  variationDetailsPanelVisible:
    () =>
    ({ getState }) => {
      const { detailsVisibleForVariationId } = getState();
      return !!detailsVisibleForVariationId;
    },
  changeClassifiedGenerationTool:
    (updatedVariation) =>
    ({ getState, setState }) => {
      const { generationResults } = getState();
      const result = generationResults.find((r) =>
        includesNestedVariation(r.variations, updatedVariation.id)
      );

      if (!result) {
        return;
      }

      const newVariations = updateNestedVariation(result.variations, updatedVariation);

      // Replace "result" object in array
      const newResults = generationResults.map((r) =>
        r.id === result.id
          ? {
              ...r,
              variations: newVariations,
            }
          : r
      );
      setState({ generationResults: newResults });
    },

  updateAllProjectVariations:
    ({ projectId, scoringMethod }) =>
    async ({ dispatch }, { notifications }) => {
      try {
        await updateAllProjectVariations({
          projectId,
          scoringMethod,
        });

        await dispatch(ACTIONS.queryGenerationResults());
      } catch (error) {
        notifications.displayError(error);
      }
    },

  rateVariation:
    (id, rating, variation, feedback) =>
    async ({ setState, getState }, { notifications }) => {
      try {
        if (rating === VARIATION_RATING.thumbsDown && !feedback) {
          const { reasons } = await variationFeedbackReasons(
            id,
            variationRatingTextToValue(rating)
          );
          return reasons;
        }
        const data = await rateVariation({
          id,
          rating: variationRatingTextToValue(rating),
          feedback,
        });
        const { generationResults } = getState();
        const newResults = updatePropertyInNestedVariationById(
          generationResults,
          id,
          'rating',
          rating
        );
        setState({ generationResults: newResults });

        return JSON.parse(data);
      } catch (error) {
        notifications.displayError(error);
        return null;
      }
    },

  clearVariationRatings:
    (projectId, generationTool) =>
    async ({ getState, setState, dispatch }, { notifications }) => {
      try {
        await deleteVariationRatings({ projectId, generationTool });
        const { generationResults } = getState();
        const newResults = generationResults.map((generationResult) => {
          const variations = clearRatingForNestedVariationsByGenerationTool(
            generationResult.variations,
            generationTool
          );

          return {
            ...generationResult,
            variations,
          };
        });
        setState({ generationResults: newResults, didClearRatings: true });

        notifications.displaySuccess('Rating history cleared');
        return true;
      } catch (error) {
        notifications.displayError(error);
        return false;
      }
    },

  setRatingsBannerShown:
    (show) =>
    ({ setState }) => {
      setState({ ratingsBannerShown: show });
    },

  setDidClearRatings:
    (value) =>
    ({ setState }) => {
      setState({ didClearRatings: value });
    },

  updateVariationImageMeta: (id, imageMeta) => async () => {
    try {
      await updateVariationImageMeta({ id, imageMeta });
      return true;
    } catch {
      return false;
    }
  },

  setVariationWarning:
    (value) =>
    ({ setState }) => {
      setState({ variationWarning: value });
    },

  saveImageVariation: (originalVariationId, encodedImage) => async () => {
    const imageVariation = await saveImageVariation({ originalVariationId, encodedImage });
    return imageVariation;
  },

  publishVariations:
    ({ variationIds, dataSource, accountId, assetName }) =>
    async ({ dispatch }) => {
      const result = await publishVariations({ variationIds, dataSource, accountId, assetName });
      result.forEach((updatedVariation) => {
        dispatch(
          PRIVATE_ACTIONS.updateVariationInGenerationResults({
            id: updatedVariation.id,
            exportInfo: updatedVariation.exportInfo,
          })
        );
      });
    },
};

export const ListViewStore = createStore({
  initialState: INITIAL_STATE,
  actions: ACTIONS,
  name: 'document-list-view',
});
